import { v4 as uuid } from 'uuid';
import { getLocaleStore } from '@/i18n/index';
import { mapRange } from '@/utils/math-kit';
import {
    LayerType,
    MaterialIdsType,
    TextAlignType,
    TextShapeType,
    TextWritingType,
    type ILayerProps,
    type ISize,
    type ITextLayerProps,
} from '@/version-types';
import { DEFAULT_FONT_FAMILY } from '@/version-types/font';
import { DEFAULT_LINE_HEIGHT_FACTOR } from '../constants';
import { type ShapeTextProps } from '../types';

// 默认参数
export const defaultShapeParams = {
    /** 强度 [-360, 360] */
    bendAngle: 0,
    /** 大小 [0, 100] */
    spacing: 0,
    /** 起始角度, 起始位置 [-1, 1] */
    positionOffset: 0,
    /** 长度 [-1, 1] */
    firstMarginRatio: 0,
    /** 离心率, 边数, 弧度, 强度 [0, 1] */
    progress: 0,
    /** 反转路径 */
    reverse: false,
    /** 长宽比 [-1, 1] */
    aspectRatio: 0,
    /** 相对高度 [0,1] */
    relativeHeight: 0,
};

/** 形状对应的plist目录 */
export const shapePlistConfigID = {
    [TextShapeType.curve]: 1,
    [TextShapeType.ellipse]: 2,
    [TextShapeType.square]: 4,
    [TextShapeType.polygon]: 16,
    [TextShapeType.triangle]: 16,
    [TextShapeType.heart]: 32,
    [TextShapeType.rectangle]: 64,
    [TextShapeType.tilde]: 128,
    [TextShapeType.waveShaped]: 'waveShaped',
    [TextShapeType.bowShaped]: 'bowShaped',
    [TextShapeType.curved]: 'curved',
    [TextShapeType.hexagon]: 'hexagon',
    [TextShapeType.upperTrapezoid]: 'upperTrapezoid',
    [TextShapeType.upperArc]: 'upperArc',
    [TextShapeType.trapezoid]: 'trapezoid',
    [TextShapeType.convexShape]: 'convexShape',
    [TextShapeType.lowerTrapezoid]: 'lowerTrapezoid',
    [TextShapeType.lowerArc]: 'lowerArc',
};

/** 是否开启长宽比 */
export const enableAspectRatioShapeTypes = {
    [TextShapeType.square]: true,
    [TextShapeType.rectangle]: true,
};

// value ui控件值
// range ui展示范围
// ratio 底层取值范围
// param 底层参数值
// 变形参数配置
export const getShapeTypeConfig = (): Record<TextShapeType, ShapeTextProps[]> => {
    const { $t } = getLocaleStore();
    return {
        /**
         * 路径
         */
        [TextShapeType.curve]: [
            {
                param: 'bendAngle',
                label: $t((v) => v.textLayer.strength),
                value: -100,
                range: [-360, 360],
                ratio: [-360, 360],
            },
        ],
        [TextShapeType.ellipse]: [
            {
                param: 'spacing',
                label: $t((v) => v.textLayer.size),
                value: 40,
                range: [0, 100],
                ratio: [0, 100],
            },
            {
                param: 'positionOffset',
                label: $t((v) => v.textLayer.startAngel),
                value: 0,
                range: [-180, 180],
                ratio: [-1, 1],
            },
            {
                param: 'firstMarginRatio',
                label: $t((v) => v.textLayer.widthLength),
                value: 180,
                range: [0, 360],
                ratio: [1, -1],
            },
            {
                param: 'progress',
                label: $t((v) => v.textLayer.eccentricity),
                value: 0,
                range: [0, 100],
                ratio: [0, 1],
            },
            {
                param: 'reverse',
                label: $t((v) => v.textLayer.reversePath),
                value: false,
                isBool: true,
            },
        ],
        [TextShapeType.square]: [
            {
                param: 'spacing',
                label: $t((v) => v.textLayer.size),
                value: 40,
                range: [0, 100],
                ratio: [0, 100],
            },
            {
                param: 'aspectRatio',
                label: $t((v) => v.textLayer.widthHeightRatio),
                value: Math.floor(mapRange(0, [-1, 1], [20, 500])),
                range: [20, 500],
                ratio: [-1, 1],
            },
            {
                param: 'positionOffset',
                label: $t((v) => v.textLayer.startPosition),
                value: Math.floor(mapRange(0, [-1, 1], [-100, 100])),
                range: [-100, 100],
                ratio: [-1, 1],
            },
            {
                param: 'firstMarginRatio',
                label: $t((v) => v.textLayer.widthLength),
                value: Math.floor(mapRange(0, [1, -1], [0, 100])),
                range: [0, 100],
                ratio: [1, -1],
            },
            {
                param: 'reverse',
                label: $t((v) => v.textLayer.reversePath),
                value: true,
                isBool: true,
            },
        ],
        [TextShapeType.polygon]: [
            {
                param: 'spacing',
                label: $t((v) => v.textLayer.size),
                value: 40,
                range: [0, 100],
                ratio: [0, 100],
            },
            {
                param: 'progress',
                label: $t((v) => v.textLayer.sideLength),
                value: 5,
                range: [3, 10],
                ratio: [0, 1],
            },
            {
                param: 'positionOffset',
                label: $t((v) => v.textLayer.startPosition),
                value: Math.floor(mapRange(0, [-1, 1], [-100, 100])),
                range: [-100, 100],
                ratio: [-1, 1],
            },
            {
                param: 'firstMarginRatio',
                label: $t((v) => v.textLayer.widthLength),
                value: Math.floor(mapRange(0, [1, -1], [0, 100])),
                range: [0, 100],
                ratio: [1, -1],
            },
        ],
        [TextShapeType.triangle]: [
            {
                param: 'spacing',
                label: $t((v) => v.textLayer.size),
                value: 40,
                range: [0, 100],
                ratio: [0, 100],
            },
            {
                param: 'progress',
                label: $t((v) => v.textLayer.sideLength),
                value: 3,
                range: [3, 10],
                ratio: [0, 1],
            },
            {
                param: 'positionOffset',
                label: $t((v) => v.textLayer.startPosition),
                value: Math.floor(mapRange(0, [-1, 1], [-100, 100])),
                range: [-100, 100],
                ratio: [-1, 1],
            },
            {
                param: 'firstMarginRatio',
                label: $t((v) => v.textLayer.widthLength),
                value: Math.floor(mapRange(0, [1, -1], [0, 100])),
                range: [0, 100],
                ratio: [1, -1],
            },
        ],
        [TextShapeType.heart]: [
            {
                param: 'spacing',
                label: $t((v) => v.textLayer.size),
                value: 40,
                range: [0, 100],
                ratio: [0, 100],
            },
            {
                param: 'positionOffset',
                label: $t((v) => v.textLayer.startPosition),
                value: Math.floor(mapRange(0, [-1, 1], [-100, 100])),
                range: [-100, 100],
                ratio: [-1, 1],
            },
            {
                param: 'firstMarginRatio',
                label: $t((v) => v.textLayer.widthLength),
                value: Math.floor(mapRange(0, [1, -1], [0, 100])),
                range: [0, 100],
                ratio: [1, -1],
            },
            {
                param: 'reverse',
                label: $t((v) => v.textLayer.reversePath),
                value: false,
                isBool: true,
            },
        ],
        [TextShapeType.rectangle]: [
            {
                param: 'spacing',
                label: $t((v) => v.textLayer.size),
                value: 40,
                range: [0, 100],
                ratio: [0, 100],
            },
            {
                param: 'progress',
                label: $t((v) => v.textLayer.roundRadius),
                value: Math.floor(mapRange(0.5, [1, 0], [0, 180])),
                range: [0, 180],
                ratio: [1, 0],
            },

            {
                param: 'aspectRatio',
                label: $t((v) => v.textLayer.widthHeightRatio),
                value: Math.floor(mapRange(0, [-1, 1], [20, 500])),
                range: [20, 500],
                ratio: [-1, 1],
            },
            {
                param: 'positionOffset',
                label: $t((v) => v.textLayer.startPosition),
                value: Math.floor(mapRange(0, [-1, 1], [-100, 100])),
                range: [-100, 100],
                ratio: [-1, 1],
            },
            {
                param: 'firstMarginRatio',
                label: $t((v) => v.textLayer.widthLength),
                value: Math.floor(mapRange(0, [1, -1], [0, 100])),
                range: [0, 100],
                ratio: [1, -1],
            },
        ],
        [TextShapeType.tilde]: [
            {
                param: 'spacing',
                label: $t((v) => v.textLayer.size),
                value: 40,
                range: [0, 100],
                ratio: [0, 100],
            },
            {
                param: 'firstMarginRatio',
                label: $t((v) => v.textLayer.startPosition),
                value: Math.floor(mapRange(0, [-1, 1], [0, 100])),
                range: [0, 100],
                ratio: [-1, 1],
            },
            {
                param: 'progress',
                label: $t((v) => v.textLayer.strength),
                value: Math.floor(mapRange(0, [0, 1], [0, 180])),
                range: [0, 180],
                ratio: [0, 1],
            },
            {
                param: 'reverse',
                label: $t((v) => v.textLayer.reversePath),
                value: false,
                isBool: true,
            },
        ],
        /**
         * 形状
         */
        [TextShapeType.curved]: [
            {
                param: 'progress',
                label: $t((v) => v.textLayer.strength),
                value: 30,
                range: [-100, 100],
                ratio: [0, 1],
            },
        ],
        [TextShapeType.lowerArc]: [
            {
                param: 'progress',
                label: $t((v) => v.textLayer.strength),
                value: 100,
                range: [-100, 100],
                ratio: [0, 1],
            },
        ],
        [TextShapeType.upperArc]: [
            {
                param: 'progress',
                label: $t((v) => v.textLayer.strength),
                value: 100,
                range: [-100, 100],
                ratio: [0, 1],
            },
        ],
        [TextShapeType.bowShaped]: [
            {
                param: 'progress',
                label: $t((v) => v.textLayer.strength),
                value: 100,
                range: [-100, 100],
                ratio: [0, 1],
            },
        ],
        [TextShapeType.waveShaped]: [
            {
                param: 'progress',
                label: $t((v) => v.textLayer.strength),
                value: 70,
                range: [-100, 100],
                ratio: [0, 1],
            },
        ],
        [TextShapeType.trapezoid]: [
            {
                param: 'progress',
                label: $t((v) => v.textLayer.strength),
                value: 70,
                range: [-100, 100],
                ratio: [0, 1],
            },
            {
                param: 'relativeHeight',
                label: $t((v) => v.textLayer.relativeHeight),
                value: 20,
                range: [-100, 100],
                ratio: [0, 1],
            },
        ],
        [TextShapeType.hexagon]: [
            {
                param: 'progress',
                label: $t((v) => v.textLayer.strength),
                value: 80,
                range: [-100, 100],
                ratio: [0, 1],
            },
            {
                param: 'relativeHeight',
                label: $t((v) => v.textLayer.relativeHeight),
                value: 20,
                range: [-100, 100],
                ratio: [0, 1],
            },
        ],
        [TextShapeType.upperTrapezoid]: [
            {
                param: 'progress',
                label: $t((v) => v.textLayer.strength),
                value: 100,
                range: [-100, 100],
                ratio: [0, 1],
            },
            {
                param: 'relativeHeight',
                label: $t((v) => v.textLayer.relativeHeight),
                value: 50,
                range: [-100, 100],
                ratio: [0, 1],
            },
        ],
        [TextShapeType.lowerTrapezoid]: [
            {
                param: 'progress',
                label: $t((v) => v.textLayer.strength),
                value: 100,
                range: [-100, 100],
                ratio: [0, 1],
            },
            {
                param: 'relativeHeight',
                label: $t((v) => v.textLayer.relativeHeight),
                value: 50,
                range: [-100, 100],
                ratio: [0, 1],
            },
        ],
        [TextShapeType.convexShape]: [
            {
                param: 'progress',
                label: $t((v) => v.textLayer.strength),
                value: 50,
                range: [0, 100],
                ratio: [0, 1],
            },
        ],
    };
};

/**
 * 获取默认的变形文字图层，用于快速创建默认变形文字图层
 */
export const getDefaultShapeTextLayerForShortcut = (stageSize: ISize, materialID: string, isDarkBG: boolean) => {
    const { locale } = getLocaleStore();
    const isCN = locale === 'zh-Hans';
    const intlCdnUrl = process.env.REACT_APP_STATIC_CDN_URL;

    const screenshotURL = isCN
        ? isDarkBG
            ? 'https://xiuxiu-pro.meitudata.com/poster/b5de4e0cc2e81999ca6c582b94d165aa.png'
            : 'https://xiuxiu-pro.meitudata.com/poster/34bffb6c10c280684ff0906a4c231047.png'
        : isDarkBG
          ? `${intlCdnUrl}/poster/0f6cf0fbaa99edabe7103aa12925b6a9.png`
          : `${intlCdnUrl}/poster/3a9d57eabc788acba84de7ee39ac4cdd.png`;

    const materialIds = { [MaterialIdsType.TEXT_TRANSFORM]: [materialID] };

    const { width: stageWidth, height: stageHeight } = stageSize;
    const size = isCN ? { width: 3253, height: 1406 } : { width: 3973, height: 1527 };

    const defaultLayerData: ITextLayerProps = {
        fontSize: 438,
        text: isCN ? '双击编辑文字' : 'Tap twice to edit',
        color: isDarkBG ? '#ffffffff' : '#000000ff',
        fontFamily: DEFAULT_FONT_FAMILY().name,
        lineSpacing: DEFAULT_LINE_HEIGHT_FACTOR,
        letterSpacing: 0,
        bold: false,
        italic: false,
        lineThrough: false,
        underline: false,
        textAlign: TextAlignType.CENTER,
        writingMode: TextWritingType.HORIZONTAL_TB,
        textGlow: [],
        textShadow: [],
        textStroke: [],
        textBackground: [],
        textGradient: {
            colors: [],
            angle: 0,
        },
        listStyle: '',
        shapeExtension: {
            curveTextType: 1,
            bendAngle: -100,
            spacing: 0,
            positionOffset: 0,
            firstMarginRatio: 0,
            progress: 0,
            reverse: false,
            aspectRatio: 0,
        },
        screenshotUrl: screenshotURL,
    };

    // 提取最小字号 6，以及倍数 0.073
    const fontSize = Math.max(6, Number((stageWidth * 0.073).toFixed(0)));
    const ratio = fontSize / defaultLayerData.fontSize;

    defaultLayerData.fontSize = fontSize;

    const width = Number((size.width * ratio).toFixed(0));
    const height = Number((size.height * ratio).toFixed(0));

    const top = (stageHeight - height) / 2;
    const left = (stageWidth - width) / 2;

    const layer: ILayerProps = {
        id: uuid(),
        layerType: LayerType.TEXT_LAYER,
        width,
        height,
        top,
        left,
        fitWidth: 0,
        fitHeight: 0,
        rotate: 0,
        opacity: 1,
        lock: false,
        shine: true,
        hidden: false,
        title: '',
        flipH: false,
        flipV: false,
        data: defaultLayerData,
        materialIds,
        children: [],
        __runtime: {
            isInit: true,
        },
    };

    return layer;
};
